import {
  Button,
  Text,
  Loader,
  Stack,
  Center,
  Box,
  Textarea,
  Group,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import {
  displayContinueMessage,
  displayErrorMessage,
  displayServerErrorMessage,
} from "../shared/ErrorMessage";
import { initialize, updateField } from "../slices/form/connectGuest";
import { type ReactElement, useEffect } from "react";
import { useLocation } from "wouter";
import { Logo } from "../shared/components/Logo";
import {
  ConnectGuestRequest,
  useConnectGuestMutation,
  useGetGuestsForRsvpQuery,
} from "../slices/api/guestsApi";
import { getLinkToVentGuest } from "../shared/links";
import { GuestStatus } from "../slices/types";
import { GuestStatusSegmentedControl } from "../shared/components/GuestsStatusSegmentedControl";

interface Props {
  ventId: string;
  ventHasCustomFields: boolean;
  ventAllowsPlusOnes: boolean;
  ventHasActivities: boolean;
  ventName: string;
  onClose: () => void;
}
const ConnectGuest = (props: Props) => {
  const { data } = useAppSelector((state: RootState) => state.connectGuestForm);
  const [_, navigate] = useLocation();

  const { data: getGuestsForRsvpResponse } = useGetGuestsForRsvpQuery({
    ventId: props.ventId,
  });

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (getGuestsForRsvpResponse) {
      // Default status to Accepted since that is the most common case.
      const plusOnes: Record<string, { name: string; status: GuestStatus }> =
        {};
      Object.keys(getGuestsForRsvpResponse.plusOnes || {}).forEach(
        (guestId) => {
          plusOnes[guestId] = {
            name: getGuestsForRsvpResponse.plusOnes[guestId].name,
            status: "a" as GuestStatus,
          };
        },
      );
      dispatch(
        initialize({
          message: "",
          primaryGuestId: getGuestsForRsvpResponse.primaryGuestId,
          primaryGuestName: getGuestsForRsvpResponse.primaryGuestName,
          primaryGuestStatus: "a",
          plusOnes,
        }),
      );
    }
  }, [getGuestsForRsvpResponse]);

  const [connectGuest, { isLoading: isConnecting }] = useConnectGuestMutation();

  const handleSubmit = async () => {
    if (!data) {
      displayErrorMessage("data should not be null");
      return;
    }
    const plusOnes: Record<string, GuestStatus> = {};
    Object.keys(data.plusOnes || {}).forEach((guestId) => {
      plusOnes[guestId] = data.plusOnes[guestId].status;
    });

    const request: ConnectGuestRequest = {
      ventId: props.ventId,
      status: data.primaryGuestStatus!,
      message: data.message ?? "",
      plusOnes: plusOnes,
    };

    try {
      // If guest is already connected, then the request is a no-op other than it returns the guest ID.
      const response = await connectGuest(request).unwrap();
      navigate(getLinkToVentGuest(props.ventId, response.guestId));
      const customFieldMessage = props.ventHasCustomFields ? (
        <strong>
          The host has asked for some additional information &#8212; please
          provide that information on the next page.
        </strong>
      ) : (
        ""
      );
      const m_accepted =
        (data.message ?? "").length > 0
          ? " and sending the host a message"
          : "";
      const m_other =
        (data.message ?? "").length > 0 ? "Your message was sent. " : "";
      switch (request.status) {
        case "a":
          if (props.ventHasActivities && props.ventAllowsPlusOnes) {
            displayContinueMessage(
              <span>
                <Text>
                  Thank you for accepting the invitation{m_accepted}. You can
                  modify your response, send the host a message, add plus ones,
                  and sign up for activities after pressing Continue.
                </Text>
                {customFieldMessage}
              </span>,
            );
          } else if (props.ventHasActivities && !props.ventAllowsPlusOnes) {
            displayContinueMessage(
              <span>
                <Text>
                  Thank you for accepting the invitation{m_accepted}. You can
                  modify your response, send the host a message, and sign up for
                  activities after pressing Continue.
                </Text>
                {customFieldMessage}
              </span>,
            );
          } else if (!props.ventHasActivities && props.ventAllowsPlusOnes) {
            displayContinueMessage(
              <span>
                <Text>
                  Thank you for accepting the invitation{m_accepted}. You can
                  modify your response, send the host a message, and add plus
                  ones after pressing Continue.
                </Text>
                {customFieldMessage}
              </span>,
            );
          } else {
            displayContinueMessage(
              <span>
                <Text>
                  Thank you for accepting the invitation{m_accepted}. You can
                  modify your response and send the host a message after
                  pressing Continue.
                </Text>
                {customFieldMessage}
              </span>,
            );
          }
          break;
        case "u":
          displayContinueMessage(
            <span>
              <Text>
                {m_other}You are undecided. You can modify your response and
                send the host a message after pressing Continue.
              </Text>
              {customFieldMessage}
            </span>,
          );
          break;
        case "d":
          displayContinueMessage(
            <Text>
              {m_other}You declined the invitation. You can modify your response
              and send the host a message after pressing Continue.
            </Text>,
          );
          break;
      }
    } catch (error) {
      displayServerErrorMessage(error);
      return;
    }
  };

  if (!data) {
    return <Loader color="blue" />;
  }

  let plusOneElements: ReactElement[] = [];
  if (data.primaryGuestId !== "") {
    plusOneElements = Object.keys(data.plusOnes).map((poGuestId) => {
      return (
        <Group key={poGuestId}>
          <Text>{data.plusOnes[poGuestId].name}</Text>
          <GuestStatusSegmentedControl
            value={data.plusOnes[poGuestId].status}
            onChange={(value) => {
              const newPlusOnes: Record<
                string,
                { name: string; status: GuestStatus }
              > = { ...data.plusOnes };
              // Create a copy of the nested object too
              newPlusOnes[poGuestId] = {
                ...newPlusOnes[poGuestId],
                status: value as GuestStatus,
              };
              dispatch(
                updateField({
                  field: "plusOnes",
                  value: newPlusOnes,
                }),
              );
            }}
          />
        </Group>
      );
    });
  }

  return (
    <Center>
      <form>
        <Stack align="center" justify="center">
          <Box w={100}>
            <Logo />
          </Box>
          <Text size="lg">RSVP for {props.ventName}</Text>
          <Textarea
            mt="md"
            style={{ width: "350px" }}
            maxLength={1000}
            label="Message to Host (optional)"
            value={data.message ?? ""}
            onChange={(event) =>
              dispatch(
                updateField({
                  field: "message",
                  value: event.currentTarget.value,
                }),
              )
            }
          />

          <Stack align="flex-end" justify="left">
            <Group>
              <Text>Your response</Text>
              <GuestStatusSegmentedControl
                value={data.primaryGuestStatus}
                onChange={(value) =>
                  dispatch(
                    updateField({
                      field: "primaryGuestStatus",
                      value: value,
                    }),
                  )
                }
              />
            </Group>
            {plusOneElements}
          </Stack>

          <Group>
            <Button mt="md" mr="md" onClick={props.onClose}>
              Cancel
            </Button>
            <Button
              mt="md"
              onClick={() => handleSubmit()}
              disabled={isConnecting}
              rightSection={isConnecting ? <Loader size={14} /> : null}
            >
              Submit
            </Button>
          </Group>
        </Stack>
      </form>
    </Center>
  );
};

export default ConnectGuest;
